/**
 * ObjectInspector — split into two parts:
 *
 *   ObjectInspectorSensor  — lives inside the R3F Canvas. Listens for
 *     pointermove, raycasts against model meshes, and reports hit info
 *     via the onTooltipChange callback. Returns null from render so R3F's
 *     reconciler never tries to create Three.js objects from HTML elements.
 *
 *   InspectorTooltip  — plain React component rendered *outside* the Canvas
 *     (as a sibling in ThreeScene). Receives TooltipState and renders a
 *     fixed-position HTML overlay via ReactDOM.createPortal.
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Shared types (exported so ThreeScene can use TooltipState)
// ---------------------------------------------------------------------------

export interface MeshInfo {
  /** Total triangle count */
  triangles: number;
  /** Hex color string without '#', e.g. "e8a243". Empty string if no color. */
  colorHex: string;
  /** Display name of the material type, e.g. "Standard", "Physical" */
  materialType: string;
  metalness: number | null;
  roughness: number | null;
  opacity: number;
}

export interface TooltipState {
  /** Viewport X (clientX) */
  x: number;
  /** Viewport Y (clientY) */
  y: number;
  info: MeshInfo;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTriangleCount(geometry: THREE.BufferGeometry): number {
  if (geometry.index) {
    return geometry.index.count / 3;
  }
  const pos = geometry.attributes.position;
  return pos ? pos.count / 3 : 0;
}

function extractMeshInfo(mesh: THREE.Mesh): MeshInfo {
  const geometry = mesh.geometry as THREE.BufferGeometry;
  const triangles = Math.round(getTriangleCount(geometry));

  // Support both single and array materials — use the first one
  const rawMaterial = Array.isArray(mesh.material)
    ? mesh.material[0]
    : mesh.material;

  if (!rawMaterial) {
    return {
      triangles,
      colorHex: '',
      materialType: 'Unknown',
      metalness: null,
      roughness: null,
      opacity: 1,
    };
  }

  const mat = rawMaterial as THREE.MeshStandardMaterial;

  // Color
  const colorHex = mat.color ? mat.color.getHexString() : '';

  // Material type — strip "Mesh" prefix and "Material" suffix for brevity
  // e.g. "MeshStandardMaterial" → "Standard", "MeshPhysicalMaterial" → "Physical"
  const rawType = rawMaterial.type ?? 'Unknown';
  const materialType = rawType.replace(/^Mesh/, '').replace(/Material$/, '');

  // Standard / Physical properties
  const metalness =
    'metalness' in mat && typeof mat.metalness === 'number'
      ? mat.metalness
      : null;
  const roughness =
    'roughness' in mat && typeof mat.roughness === 'number'
      ? mat.roughness
      : null;
  const opacity = rawMaterial.opacity ?? 1;

  return { triangles, colorHex, materialType, metalness, roughness, opacity };
}

// ---------------------------------------------------------------------------
// InspectorTooltip — rendered OUTSIDE the R3F Canvas via a Portal.
// Import and use this in ThreeScene (or any regular React tree).
// ---------------------------------------------------------------------------

export function InspectorTooltip({
  tooltip,
}: {
  tooltip: TooltipState | null;
}) {
  if (!tooltip) return null;

  const { x, y, info } = tooltip;

  // Offset so the tooltip doesn't sit directly under the cursor
  const offsetX = 16;
  const offsetY = 16;

  const colorStyle = info.colorHex ? `#${info.colorHex}` : 'transparent';

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: x + offsetX,
        top: y + offsetY,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          fontFamily: 'monospace',
          fontSize: '11px',
          lineHeight: '1.5',
          backgroundColor: 'rgba(0,0,0,0.82)',
          color: '#ffffff',
          padding: '6px 10px',
          borderRadius: '5px',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          border: '1px solid rgba(255,255,255,0.15)',
          backdropFilter: 'blur(4px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        }}
      >
        {/* Row 1: color swatch + hex + material type */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '3px',
          }}
        >
          {info.colorHex && (
            <span
              style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: colorStyle,
                border: '1px solid rgba(255,255,255,0.3)',
                flexShrink: 0,
              }}
            />
          )}
          {info.colorHex && (
            <span style={{ color: '#aaa' }}>
              #{info.colorHex.toUpperCase()}
            </span>
          )}
          <span
            style={{
              color: '#60a5fa',
              fontWeight: 'bold',
              marginLeft: info.colorHex ? '4px' : '0',
            }}
          >
            {info.materialType}
          </span>
        </div>

        {/* Row 2: polygon count */}
        <div>
          <span style={{ color: '#aaa' }}>Triangles: </span>
          <span>{info.triangles.toLocaleString()}</span>
        </div>

        {/* Row 3: metalness + roughness */}
        {(info.metalness !== null || info.roughness !== null) && (
          <div style={{ display: 'flex', gap: '12px' }}>
            {info.metalness !== null && (
              <span>
                <span style={{ color: '#aaa' }}>Metal: </span>
                {info.metalness.toFixed(2)}
              </span>
            )}
            {info.roughness !== null && (
              <span>
                <span style={{ color: '#aaa' }}>Rough: </span>
                {info.roughness.toFixed(2)}
              </span>
            )}
          </div>
        )}

        {/* Row 4: opacity (only shown when not fully opaque) */}
        {info.opacity < 0.999 && (
          <div>
            <span style={{ color: '#aaa' }}>Opacity: </span>
            {info.opacity.toFixed(2)}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// ObjectInspectorSensor — R3F canvas component.
// Must return null — never returns HTML elements, so R3F's reconciler is safe.
// Reports hover info upward via onTooltipChange.
// ---------------------------------------------------------------------------

interface ObjectInspectorSensorProps {
  enabled: boolean;
  /** Ref to the group containing all pickable model meshes */
  modelGroupRef: React.RefObject<THREE.Group | null>;
  /** Called with the current tooltip state, or null when not hovering a mesh */
  onTooltipChange: (tooltip: TooltipState | null) => void;
}

export function ObjectInspectorSensor({
  enabled,
  modelGroupRef,
  onTooltipChange,
}: ObjectInspectorSensorProps) {
  const { camera, gl } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());

  // Gather all pickable meshes from the model group (same pattern as MeasurementSystem)
  const getPickableObjects = useCallback((): THREE.Mesh[] => {
    const meshes: THREE.Mesh[] = [];
    if (modelGroupRef.current) {
      modelGroupRef.current.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          meshes.push(child as THREE.Mesh);
        }
      });
    }
    return meshes;
  }, [modelGroupRef]);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!enabled) return;

      const rect = gl.domElement.getBoundingClientRect();
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);

      const pickable = getPickableObjects();
      if (pickable.length === 0) {
        onTooltipChange(null);
        return;
      }

      const intersects = raycaster.current.intersectObjects(pickable, false);

      if (intersects.length > 0) {
        const hit = intersects[0].object as THREE.Mesh;
        const info = extractMeshInfo(hit);
        onTooltipChange({ x: event.clientX, y: event.clientY, info });
      } else {
        onTooltipChange(null);
      }
    },
    [enabled, camera, gl, getPickableObjects, onTooltipChange],
  );

  // Cursor style
  useEffect(() => {
    if (enabled) {
      gl.domElement.style.cursor = 'crosshair';
    } else {
      gl.domElement.style.cursor = '';
      onTooltipChange(null);
    }
    return () => {
      gl.domElement.style.cursor = '';
    };
  }, [enabled, gl, onTooltipChange]);

  // Attach / detach event listeners
  useEffect(() => {
    const canvas = gl.domElement;
    if (!enabled) return;

    const handlePointerLeave = () => onTooltipChange(null);

    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerleave', handlePointerLeave);
      onTooltipChange(null);
    };
  }, [enabled, gl, handlePointerMove, onTooltipChange]);

  // Always return null — this component has no scene-graph presence
  return null;
}
